import { RichLabel } from "@patient-watch/forms";
import { Card } from "@patient-watch/ui";
import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import type { InputT } from "@patient-watch/types";
import { useFormContext } from "react-hook-form";

import CountStepper from "./CountStepper";

interface Props {
    input: InputT;
    /** Called with each new count so the saved entry values follow the form. */
    onStoreChange?: (value: number | null) => void;
}

function toCount(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Highest count when the input row sets no max_value. */
const DEFAULT_MAX = 999;

/**
 * A whole-number count with plus and minus buttons. On its own it is a card with the question and the
 * stepper. Several of them under a `sum_equals` page rule are the counts that must add up to a total.
 */
const CountInput = ({ input, onStoreChange }: Props) => {
    const { control } = useFormContext();
    const label = input.input_label ?? "";
    const min = input.min_value ?? 0;
    const max = input.max_value ?? DEFAULT_MAX;

    return (
        <FormField
            control={control}
            name={input.input_id}
            render={({ field }) => (
                <FormItem>
                    <Card className="flex flex-col gap-2 bg-white/70 p-4" data-testid={`count-${input.input_id}`}>
                        <div className="flex justify-between items-center gap-3">
                            <FormLabel className="flex-1 min-w-0 font-medium leading-snug">
                                <RichLabel>{label}</RichLabel>
                                {!input.is_optional && <span className="ml-0.5 text-red-500">*</span>}
                            </FormLabel>
                            <CountStepper
                                inputRef={field.ref}
                                name={field.name}
                                value={toCount(field.value)}
                                min={min}
                                max={max}
                                label={label}
                                onBlur={field.onBlur}
                                onChange={(next) => {
                                    field.onChange(next);
                                    onStoreChange?.(next);
                                }}
                            />
                        </div>
                        {input.input_description ? (
                            <FormDescription>
                                <RichLabel>{input.input_description}</RichLabel>
                            </FormDescription>
                        ) : null}
                        <FormMessage />
                    </Card>
                </FormItem>
            )}
        />
    );
};

export default CountInput;
